/**
 * ListeningWave — the in-composer recording state: an animated equalizer, the
 * live transcript as it is recognized, and a stop button.
 *
 * Swaps IN PLACE of the composer's textarea while the mic is on, rather than
 * floating over it (that is `ListeningOverlay`, for surfaces where dictation is
 * a modal moment). Stopping leaves the transcribed text in the composer — the
 * host has been syncing it all along — so the user lands back on a normal,
 * editable textarea pre-filled with what they said.
 *
 * The transcript WRAPS and grows rather than truncating to one line, because
 * anything past the first breath would otherwise scroll out of view unread. It
 * grows to `maxHeight` and then scrolls PINNED TO THE TAIL, so the newest words
 * stay in view: a reader tracks what they are saying now, not what they opened
 * with. Pass the host composer's own typing cap as `maxHeight` and the strip and
 * the textarea agree — no height jump at the swap.
 *
 * The equalizer and the stop button anchor to the BOTTOM (`items-end`), beside
 * the line currently being written, so they hold still while the text grows
 * upward.
 *
 * The bars ride `animate-voicebar` from the preset, which the reduced-motion
 * plugin already gates — a moving level meter is decorative, and its job (a
 * recording is live) is carried by the transcript and the stop button too.
 *
 * No built-in tooltip on Stop, per the `ActionIconTile` precedent: a real
 * `aria-label` here, the hint in the consumer's tooltip.
 *
 *   {recording
 *     ? <ListeningWave transcript={interim} onStop={stop} maxHeight={160} />
 *     : <textarea … />}
 */
import * as React from "react";
import { Square } from "lucide-react";
import { cn } from "../lib/cn";
import { Skeleton } from "../primitives/Skeleton";

/** Five bars, each offset so the row reads as a level meter, not a pulse. */
const BARS = [0, 1, 2, 3, 4];

export interface ListeningWaveProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** The live transcript so far. Empty shows `placeholder`. */
  transcript: string;
  /** Stop recording and keep what was said. */
  onStop: () => void;
  /**
   * The host composer's typing cap in px. The strip grows to it, then scrolls
   * pinned to the tail.
   */
  maxHeight: number;
  /** Shown before the first words land. Default `"Listening…"`. */
  placeholder?: string;
  /** Accessible name for the stop button. Default `"Stop voice input"`. */
  stopLabel?: string;
  /**
   * The mic is opening and no audio has been heard yet. The meter holds still
   * (an animated meter with no signal behind it is a lie about what the
   * microphone is doing) and the transcript line shows a placeholder.
   *
   * Distinct from the empty state: an empty `transcript` with the mic already
   * live shows `placeholder` ("Listening…"), which is a true statement. This is
   * the moment before that, when the app cannot yet claim to be listening.
   */
  connecting?: boolean;
  /**
   * Classes for the equalizer bars, so a surface can tint the meter to its own
   * action accent. `className` lands on the root and cannot reach them.
   * Defaults to `bg-primary`.
   */
  barClassName?: string;
}

export const ListeningWave = React.forwardRef<HTMLDivElement, ListeningWaveProps>(
  function ListeningWave(
    {
      transcript,
      onStop,
      maxHeight,
      placeholder = "Listening…",
      stopLabel = "Stop voice input",
      connecting = false,
      barClassName,
      className,
      ...props
    },
    ref,
  ) {
    const scrollRef = React.useRef<HTMLSpanElement>(null);

    /*
     * Follow the tail: once the transcript outgrows the cap, keep the newest
     * words in view.
     */
    React.useEffect(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, [transcript]);

    return (
      <div ref={ref} className={cn("flex items-end gap-3 px-4 py-3", className)} {...props}>
        <div className="flex items-center gap-[3px] pb-[3px]" aria-hidden="true">
          {BARS.map((i) => (
            <span
              key={i}
              className={cn(
                "h-4 w-[3px] origin-center rounded-full bg-primary",
                // Still while connecting: a moving meter with no audio behind
                // it misreports what the microphone is doing.
                connecting ? "opacity-40" : "animate-voicebar",
                barClassName,
              )}
              /*
               * Per-bar phase offset. An inline style, not a Tailwind arbitrary
               * `delay-[…]`: the value is computed per index, so a class string
               * would be unscannable by the JIT and compile to nothing.
               */
              style={{ animationDelay: `${i * 0.12}s` }}
            />
          ))}
        </div>
        <span
          ref={scrollRef}
          style={{ maxHeight }}
          className="min-w-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words font-sans text-[14px] leading-relaxed text-muted-foreground"
          aria-live="polite"
        >
          {connecting ? (
            <Skeleton aria-hidden="true" className="h-3.5 w-40 rounded-full" />
          ) : (
            transcript || placeholder
          )}
        </span>
        <button
          type="button"
          onClick={onStop}
          aria-label={stopLabel}
          className={cn(
            "inline-flex h-8 w-8 flex-none items-center justify-center rounded-full",
            "bg-destructive text-destructive-foreground",
            "transition-opacity duration-150 hover:opacity-90",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
        >
          <Square className="h-3 w-3 fill-current" aria-hidden="true" />
        </button>
      </div>
    );
  },
);
